import { Injectable, Inject, InjectionToken, NgZone } from '@angular/core';
import {Router} from '@angular/router';
import { Auth0Client, IdToken } from '@auth0/auth0-spa-js';
import {WindowRefService} from '../shared/window.service';
import {environment} from '../../environments/environment';
import {AuthToken} from './auth.config';
import {firstValueFrom, ReplaySubject} from 'rxjs';
import { AppStabilityService } from '../shared/app-stability.service';
import { HttpClient } from '@angular/common/http';
import {getGa} from '../../declarations/ga';
import tenantConfigs from '~app/shared/map/layer/layer-definitions';
import { TenantConfig } from '~app/shared/map/layer/layer-definitions/tenant-config.type';
import { MarkerMode } from '~app/shared/map/map.service';

export type Status = 'init' | 'logging_in' | 'logging_out' | 'standby' | 'logged_in' | 'logged_out';
export type LoginEvent = {
  token: string,
  tenant: number,
};

export type WebAuthProvider = () => Auth0Client;
export const WEBAUTH_PROVIDER = new InjectionToken<WebAuthProvider>('WebAuthProvider');

interface LoggedUserRights {
  apiReadPermissions: string[];
  apiWritePermissions: string[];
  legacyApiReadPermissions: string[];
  legacyApiWritePermissions: string[];
  legacyUiPermissions: string[];
  uiReadPermissions: string[];
  uiWritePermissions: string[];
}

/**
 * Authorization service. Communicates with Auth0 and application and LocalStorage and application.
 * Access token management, authorization, authentication etc are handled here.
 */
@Injectable()
export class AuthService {
  /**
   * Local reference to window object, injected via service
   */
  private _window: Window;
  private status: Status = 'init';
  private auth0 = new ReplaySubject<Auth0Client>(1);

  private idToken: IdToken;
  private expiresAt: Date;
  private uiRights: Set<string> = new Set();

  private _login = new ReplaySubject<LoginEvent>(1);
  public login$ = this._login.asObservable();

  /**
   * Default constructor
   * @param {Router} router - Angular router instance
   * @param windowRef
   * @param auth0
   * @param http
   */
  constructor(
      public router: Router,
      windowRef: WindowRefService,
      @Inject(WEBAUTH_PROVIDER) authProvider: WebAuthProvider,
      appStability: AppStabilityService,
      private http: HttpClient,
      private ngZone: NgZone,
    ) {
    console.log('AuthService constructed');
    this._window = windowRef.nativeWindow;
    appStability.whenFirstStable().then(() => {
      console.log('App is stable, initializing Auth0');
      this.auth0.next(authProvider());
      setInterval(() => {
        this.renewToken(false);
      }, 30 * 60 * 1000);
      this.renewToken(false);
    });
  }

  public getToken(): string {
    return this.idToken?.__raw;
  }

  public getTenant(): number {
    return this.idToken?.['https://quantummaps.com/app_metadata'].tenant_id;
  }

  public getTenantKey(): string {
    return this.idToken?.['https://quantummaps.com/app_metadata'].tenant_key;
  }

  public getTriTenant(): string {
    return this.idToken?.['https://quantummaps.com/app_metadata'].tri_tenant;
  }

  public getSubject(): string {
    return this.idToken?.sub;
  }

  public getEmail(): string {
    return this.idToken?.email;
  }

  public getTenantConfig(markerMode?: MarkerMode): TenantConfig {
    return tenantConfigs[this.getTenant()](markerMode);
  }

  /**
   * Ask Auth0 to perform authentication.
   */
  public login(): void {
    console.log('auth login');
    this.renewToken(true);
  }

  public getStatus() {
    return this.status;
  }

  /**
   * This method is called after Auth0 login callback is returned.
   * Get token hash from URL and initialize session and navigate to default route
   * TODO navigate to route that was requested before authentication process kicked in.
   */
  public async handleAuthenticationResult(): Promise<void> {
    try {
      const auth0 = await firstValueFrom(this.auth0);
      const authResult = await auth0.handleRedirectCallback();
      console.log(authResult);
      await this.setSession();
      this.router.navigate(['/home']);
    } catch (err) {
      console.log(err);
      alert(`Error: ${err.error}. Check the console for further details.`);
      await this.logout();
    }
  }

  public getPermissions(): Set<string> {
    return this.uiRights;
  }

  /**
   * Check if the user has the given UI permission
   * @param {string} permission
   * @returns {boolean}
   */
  public hasPermission(permission: string): boolean {
    return this.getPermissions().has(permission);
  }

  /**
   * Logout method. After logout, redirect to login. Removes all token stuff from LocalStorage.
   */
  public async logout(): Promise<void> {
    this.status = 'logged_out';
    // Remove tokens and expiry time from localStorage (clean up for old session handling)
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('expires_at');
    localStorage.removeItem('tri_tenant');

    const auth0 = await firstValueFrom(this.auth0);
    await auth0.logout({
      returnTo: this._window.location.origin,
    });
  }

  /**
   * Check if user has valid token. Currently tests override this check.
   * @returns {boolean} - authenticated
   */
  public isAuthenticated(): boolean {
    // Check whether the current time is past the access token's expiry time
    return this.expiresAt && new Date().getTime() < this.expiresAt.getTime();
  }

  /**
   * Process an authentication result and notify subscribers of $login.
   */
  private async setSession(): Promise<void> {
    const auth0 = await firstValueFrom(this.auth0);
    this.idToken = await auth0.getIdTokenClaims();
    console.log('ID token', this.idToken);

    this.expiresAt = new Date();
    this.expiresAt.setSeconds(this.expiresAt.getSeconds() + this.idToken.exp);

    const rights = await this.http.get<LoggedUserRights>(environment.apiURL + 'loggeduserrights').toPromise();
    this.uiRights = new Set([
      rights.legacyUiPermissions || [], // contains the ui_rights from the JWT token
      rights.uiReadPermissions || [],
      rights.uiWritePermissions || [],
    ].flat().filter(right => right));

    this.status = 'logged_in';
    this._login.next({
      token: this.idToken.__raw,
      tenant: this.getTenant(),
    });
  }

  /**
   * Renew authentication token
   * @param {boolean} login
   */
  private async renewToken(login: boolean): Promise<void> {
    this.status = 'logging_in';

    const auth0 = await firstValueFrom(this.auth0);
    try {
      await auth0.getTokenSilently({
        redirect_uri: environment.silentURL,
        timeoutInSeconds: 15,
      });
    } catch (err) {
      console.log('renewToken failed', err);
      if (!this.isAuthenticated()) {
        this.status = 'logged_out';
        return auth0.loginWithRedirect({
          id_token_hint: this.getToken(),
          login_hint: this.getEmail(),
        });
      }
      // what should be the status here?
      this.status = 'logged_in';
    }

    await this.setSession();
    if (login) {
      this.ngZone.runOutsideAngular(() => {
        const interval = setInterval(() => {
          try {
            const _ga = getGa() || globalThis.ga;
            if (_ga) {
              console.log('Init GA');
              clearInterval(interval);
              _ga('create', 'UA-153296020-1', 'auto');
              _ga('set', 'dimension1', 'TENANT_' + this.getTenant());
            }
          } catch (e) {
          }
        }, 50);
      });

      this.router.navigate(['/home']);
    }
  }
}
